import cloneDeep from 'lodash/cloneDeep';

import type { LogicPageQuestion } from '@webapp/account/src/resources/logic';
import {
    createCantRateVariant,
    createNpsVariants,
    createStarVariants,
    mapVariantCol,
    variantsInColumns,
    variantsWithMatrix,
    variantsWithValues,
} from '@webapp/account/src/resources/logic/lib';
import type { QuestionResponse } from '@webapp/account/src/resources/logic/map';
import type { LogicConditionResponse } from '@webapp/account/src/resources/logic/map-logic';
import { mapQuestionRow, mapVariant } from '@webapp/account/src/resources/logic/map-logic';

import type { LogicCondition, LogicSubCondition, LogicVariant } from '@webapp/common/entities/logic';
import { initialLogicCondition, initialLogicVariant } from '@webapp/common/entities/logic';
import type { QuestionCol } from '@webapp/common/entities/question-col';
import { initialCol } from '@webapp/common/entities/question-col';
import type { QuestionRow } from '@webapp/common/entities/question-row';
import { initialRow } from '@webapp/common/entities/question-row';
import { parseJSON } from '@webapp/common/lib/utils';
import { CANTRATE_ID, CANTRATE_TEXT } from '@webapp/common/resources/constants';
import type { IQuestionCategory } from '@webapp/common/resources/entities/question';
import type { NestedItem } from '@webapp/common/resources/question';
import { LinkParamsLogicType, LogicAction, QuestionType } from '@webapp/common/resources/survey';

export const mapNested = ({
    ANSWER_ID,
    ID,
    LEVEL,
    NAME,
    PARENT_ID,
}: {
    ID: number;
    ANSWER_ID: number;
    PARENT_ID: number;
    NAME: string;
    LEVEL: number;
}): NestedItem => ({
    id: ID,
    answerId: ANSWER_ID,
    name: NAME,
    level: LEVEL,
    parentId: PARENT_ID,
});
export const getVariantsToLogic = (question: LogicPageQuestion, initialVariants: Array<Partial<LogicVariant>>) => {
    let variants: Array<LogicVariant> = [];
    const cantRateChecked = !!initialVariants.find((v) => v.id === CANTRATE_ID)?.checked;
    if (variantsInColumns.has(question.type)) {
        // TODO merge with reducer
        variants = question.cols.map((group: any) => {
            const checked = !!initialVariants.find((v) => v.id === group.id)?.checked;

            return {
                ...cloneDeep(initialLogicVariant),
                id: group.id,
                name: group.name,
                checked,
                order: group.order,
                numeric_value: group.numeric_value,
            };
        });
        if (question.params.cantRate) {
            variants.push(createCantRateVariant(cantRateChecked, question.params.cantRateText));
        }
        return variants;
    }

    switch (question.type) {
        case QuestionType.NPS:
            variants = createNpsVariants(variants);
            break;
        case QuestionType.STAR:
            variants = createStarVariants(Number(question.params.amount), variants);
            break;
        case QuestionType.MATRIX_SINGLE_ANSWER:
        case QuestionType.MATRIX_FEW_ANSWERS:
        case QuestionType.MATRIX_RATING:
            const nextVariants: Array<LogicVariant> = [];
            let modCols;

            if (question.type === QuestionType.MATRIX_RATING) {
                const list = [];

                for (let i = question.params.minAmount ?? 1; i < question.params.amount + 1; i++) {
                    list.push({ id: i });
                }

                modCols = list;
            } else {
                modCols = question.cols;
            }
            if (question.params.cantRate) {
                modCols = [...modCols, { id: CANTRATE_ID, name: question.params.cantRateText || CANTRATE_TEXT }];
            }
            modCols.forEach((col, colIdx) => {
                question.rows.forEach((row, rowIdx) => {
                    const foundedVariant = variants.find(({ id }) => {
                        return id === `${question.id}-${col.id}-${row.id}`;
                    });
                    nextVariants.push({
                        ...cloneDeep(initialLogicVariant),
                        id: `${question.id}-${col.id}-${row.id}`,
                        name: 'name' in col && col.name ? `${row.value}-${col.name}` : String(row.value ?? ''),
                        checked: Boolean(foundedVariant?.checked),
                        order: colIdx * question.rows.length + rowIdx,
                    });
                });
            });
            variants = nextVariants;
            break;
        case QuestionType.MATRIX_TEXT_ANSWER:
        case QuestionType.MATRIX_DROPDOWN_LIST:
            // в логике у этих матриц есть только столбец «Не могу оценить» (построчно)
            variants = question.params.cantRate
                ? question.rows.map((row, rowIdx) => ({
                      ...cloneDeep(initialLogicVariant),
                      id: `${question.id}-${CANTRATE_ID}-${row.id}`,
                      name: `${row.value}-${question.params.cantRateText || CANTRATE_TEXT}`,
                      checked: false,
                      order: rowIdx,
                  }))
                : [];
            break;
        default:
            variants = question.rows.map((answer) => {
                const newVariant = mapQuestionRow(answer);
                newVariant.checked = !!variants.find(({ id }) => id === newVariant.id);
                return newVariant;
            });
    }
    if (question.params.cantRate && !variantsWithMatrix.has(question.type) && variantsWithValues.has(question.type)) {
        variants.push(createCantRateVariant(false, question.params.cantRateText));
    }
    return variants.map((variant) => {
        const initialVariant = initialVariants.find(({ id }) => id === variant.id);
        return {
            ...variant,
            checked: initialVariant?.checked ?? variant.checked,
        };
    });
};

export const mapSubCondition = ({
    ID,
    LOGIC_ID,
    TYPE,
    QUESTION_ID,
    LINK_PARAM_ID,
    RESPONSES_COUNT,
    BOOL_FUNC,
    BOOL_FUNC_EXTERNAL,
    LINK_PARAM_VALUE,
    CONDITION_TYPE,
    PARAMS,
}: any): LogicSubCondition => {
    const parsedParams: { variants?: Array<any>; minCount?: number; maxCount?: number } = PARAMS
        ? parseJSON(PARAMS, {})
        : {};
    return {
        id: ID,
        logicId: LOGIC_ID,
        respondent: TYPE,
        questionId: QUESTION_ID,
        linkParamId: LINK_PARAM_ID,
        linkParamValue: LINK_PARAM_VALUE,
        responsesCount: RESPONSES_COUNT,
        boolFunc: BOOL_FUNC,
        boolFuncExternal: BOOL_FUNC_EXTERNAL,
        type: CONDITION_TYPE,
        variants: parsedParams.variants ?? [],
        minCount: parsedParams.minCount,
        maxCount: parsedParams.maxCount,
    };
};
export const mapCondition = (
    {
        AFTER_QUESTION_ID,
        FINISH_TRANSITION,
        FINISH_TRANSITION_VARIANT,
        TRANSITION_ACTION,
        BOOL_FUNC,
        BREAK_TEXT,
        COMPLETE_TEXT,
        GOAL,
        ID,
        NEXT_PAGE,
        NEXT_QUESTION,
        NEXT_QUESTIONS,
        NEXT_PAGES,
        PARAMS,
        TO_SURVEY,
        TO_WEB,
        TRANSITION_TYPE,
        TYPE,
        RESPONSES_COUNT,
        IS_EXTEDNED,
        ANSWERS_TO_HIDE,
        LINK_PARAMS_LOGIC_TYPE,
        conditions,
        COPY_URL_PARAMS,
    }: LogicConditionResponse,
    questionData: QuestionResponse,
): LogicCondition => {
    const condition: LogicCondition = {
        ...cloneDeep(initialLogicCondition),
        afterQuestionId: AFTER_QUESTION_ID,
        linkParamsLogicType:
            LINK_PARAMS_LOGIC_TYPE === LinkParamsLogicType.NOT_EQUAL
                ? LinkParamsLogicType.NOT_EQUAL
                : LinkParamsLogicType.EQUAL,
        finishTransition: FINISH_TRANSITION,
        finishTransitionVariant: FINISH_TRANSITION_VARIANT,
        answersToHide: ANSWERS_TO_HIDE ? parseJSON(ANSWERS_TO_HIDE, []) : [],
        action:
            TRANSITION_ACTION === 'HIDE'
                ? LogicAction.HIDE
                : TRANSITION_ACTION === 'ANSWERS_HIDE'
                ? LogicAction.ANSWERS_HIDE
                : LogicAction.SHOW,
        expand: true,
        isExtended: !!IS_EXTEDNED,
        id: ID,
        respondent: TYPE,
        transition: GOAL,
        toQuestion: NEXT_QUESTION,
        toQuestions: NEXT_QUESTIONS ? parseJSON(NEXT_QUESTIONS, []) : [],
        toPages: NEXT_PAGES ? parseJSON(NEXT_PAGES, []) : [],
        toPage: NEXT_PAGE,
        boolFunc: BOOL_FUNC,
        toWebsite: TO_WEB,
        completeText: COMPLETE_TEXT,
        transitionType: TRANSITION_TYPE,
        disqualText: BREAK_TEXT ? String(BREAK_TEXT) : '',
        toSurvey: TO_SURVEY,
        responsesCount: RESPONSES_COUNT,
        variants: [],
        conditions: Array.isArray(conditions) ? conditions.map((condition) => mapSubCondition(condition)) : [],
        copyUrlParams: Boolean(COPY_URL_PARAMS),
    };

    const { answers, groups, parameters: { params } = { params: null }, QUESTION_TYPE_ID } = questionData;
    const {
        amount,
        minAmount: minAmountRaw,
        cantRate,
        cantRateText,
    }: { amount: number; minAmount: number; cantRate?: boolean; cantRateText?: string } = parseJSON(params, {});
    const minAmount = minAmountRaw ?? 1;
    const questionTypeId = QUESTION_TYPE_ID;
    let variants: Array<LogicVariant> = [];

    if (PARAMS !== null && PARAMS !== '') {
        // TODO core#278
        const parsedParams = parseJSON<
            Array<LogicVariant> | { variants?: Array<LogicVariant>; minCount?: number; maxCount?: number }
        >(PARAMS, []);
        if (Array.isArray(parsedParams)) {
            variants = parsedParams;
        } else {
            variants = parsedParams.variants ?? [];
            condition.minCount = parsedParams.minCount;
            condition.maxCount = parsedParams.maxCount;
        }
    }

    if (variantsInColumns.has(questionTypeId)) {
        // TODO merge with reducer
        condition.variants = groups.map((group: any) => {
            const newVariant = mapVariantCol(group);
            newVariant.checked = !!variants.find((v) => v.id === newVariant.id);
            return newVariant;
        });
        if (cantRate) {
            condition.variants.push(
                createCantRateVariant(!!variants.find((v) => v.id === CANTRATE_ID)?.checked, cantRateText),
            );
        }
        return condition;
    }

    switch (questionTypeId) {
        case QuestionType.NPS:
            condition.variants = createNpsVariants(variants);
            break;
        case QuestionType.STAR:
            condition.variants = createStarVariants(amount, variants);
            break;
        case QuestionType.MATRIX_SINGLE_ANSWER:
        case QuestionType.MATRIX_FEW_ANSWERS:
        case QuestionType.MATRIX_RATING:
            const nextVariants: Array<LogicVariant> = [];
            let modCols;

            if (questionTypeId === QuestionType.MATRIX_RATING) {
                const list = [];

                for (let i = minAmount; i < amount + 1; i++) {
                    list.push({ ID: i });
                }

                modCols = list;
            } else {
                modCols = groups;
            }
            if (cantRate) {
                modCols = [...modCols, { ID: CANTRATE_ID }];
            }
            modCols.forEach((col, colIdx) => {
                answers.forEach((row, rowIdx) => {
                    const foundedVariant = variants.find(({ id }) => {
                        return id === `${questionData.ID}-${col.ID}-${row.ID}`;
                    });
                    nextVariants.push({
                        ...cloneDeep(initialLogicVariant),
                        id: `${questionData.ID}-${col.ID}-${row.ID}`,
                        name: '',
                        checked: Boolean(foundedVariant?.checked),
                        order: colIdx * answers.length + rowIdx,
                    });
                });
            });
            condition.variants = nextVariants;
            break;
        case QuestionType.MATRIX_TEXT_ANSWER:
        case QuestionType.MATRIX_DROPDOWN_LIST:
            // в логике у этих матриц есть только столбец «Не могу оценить» (построчно)
            condition.variants = cantRate
                ? answers.map((row: any, rowIdx: number) => ({
                      ...cloneDeep(initialLogicVariant),
                      id: `${questionData.ID}-${CANTRATE_ID}-${row.ID}`,
                      name: '',
                      checked: !!variants.find(({ id }) => id === `${questionData.ID}-${CANTRATE_ID}-${row.ID}`),
                      order: rowIdx,
                  }))
                : [];
            break;
        default:
            condition.variants = answers.map((answer: any) => {
                const newVariant = mapVariant(answer);
                newVariant.checked = !!variants.find(({ id }) => id === newVariant.id);
                return newVariant;
            });
    }

    if (cantRate && !variantsWithMatrix.has(questionTypeId) && variantsWithValues.has(questionTypeId)) {
        condition.variants.push(
            createCantRateVariant(!!variants.find((v) => v.id === CANTRATE_ID)?.checked, cantRateText),
        );
    }

    return condition;
};

export const mapCol = ({
    ANSWERS,
    EXCEPTION,
    ID,
    NAME,
    ORDER,
    NUMERIC_VALUE,
    TYPE,
    QUESTION_ID,
    PINNED,
}: any): QuestionCol => {
    const col: QuestionCol = {
        ...cloneDeep(initialCol),
        id: ID,
        name: NAME === null ? '' : NAME,
        type: TYPE,
        exception: Boolean(EXCEPTION),
        pinned: Boolean(PINNED),
        order: ORDER,
        numeric_value: NUMERIC_VALUE,
        questionId: QUESTION_ID,
    };

    try {
        col.answers = ANSWERS ? JSON.parse(ANSWERS) : []; // TODO fix backend
    } catch (e) {
        col.answers = [];
        console.error(e);
    }

    return col;
};

export const mapRow = ({
    ANSWER,
    ANSWER_TEXT,
    EXCEPTION,
    FILE_ID,
    FILE_PATH,
    ID,
    IMAGE_HEIGHT,
    IMAGE_WIDTH,
    MIME_TYPE,
    ORDER,
    TYPE,
    VIDEO_LINK,
    TEXT_BLOCK,
    FORWARD_PARENT,
    PINNED,
    ANSWER_AREAS,
    EXCLUDED,
}: any): QuestionRow => {
    const row: QuestionRow = {
        ...cloneDeep(initialRow),
        id: ID,
        value: ANSWER || '',
        type: TYPE,
        exception: !!EXCEPTION,
        fileId: FILE_ID,
        filePath: FILE_PATH || null,
        width: IMAGE_WIDTH,
        height: IMAGE_HEIGHT,
        mimeType: MIME_TYPE,
        order: ORDER,
        text: ANSWER_TEXT,
        videoLink: VIDEO_LINK,
        textBlock: TEXT_BLOCK,
        forwardParent: FORWARD_PARENT,
        pinned: PINNED,
        answerArea: ANSWER_AREAS
            ? {
                  id: ANSWER_AREAS.ID,
                  answerId: ANSWER_AREAS.ANSWER_ID,
                  x: ANSWER_AREAS.X,
                  y: ANSWER_AREAS.Y,
                  width: ANSWER_AREAS.WIDTH,
                  height: ANSWER_AREAS.HEIGHT,
                  isDefault: !!ANSWER_AREAS.IS_DEFAULT,
              }
            : undefined,
        excluded: EXCLUDED,
    };

    try {
        const value = JSON.parse(row.value as string);

        if (Array.isArray(value)) {
            row['value'] = value[0];
            row['value2'] = value[1];
            row['value3'] = value[2];
        }
    } catch (e) {}

    return row;
};

export const mapCategory = ({
    CATEGORY_MERGERS_ID,
    CATEGORY_MERGERS_NAME,
    CREATED_AT,
    ID,
    NAME,
    ORDER,
    ORIGIN,
    QUESTION_ID,
    UPDATED_AT,
    PARENT_ANSWER_ID,
    PINNED,
    EXCLUDED,
}: any): IQuestionCategory => {
    return {
        categoryMergersId: CATEGORY_MERGERS_ID,
        categoryMergersName: CATEGORY_MERGERS_NAME,
        createdAt: CREATED_AT,
        id: ID,
        name: NAME,
        order: ORDER,
        origin: ORIGIN,
        questionId: QUESTION_ID,
        updatedAt: UPDATED_AT,
        parentAnswerId: PARENT_ANSWER_ID,
        forwardParent: PARENT_ANSWER_ID,
        pinned: Boolean(PINNED),
        excluded: Boolean(EXCLUDED),
    };
};

export const deepMapQuestion = (question, questionsMapped) => {
    if (question.type === QuestionType.TEXT_BLOCK) {
        return {
            ...question,
            params: {
                ...question.params,
                textBlock:
                    typeof question.params.textBlock !== 'string'
                        ? question.params.textBlock
                        : question.params.textBlock.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                              const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                              if (!q) return '';
                              return `{Q${q.order}}`;
                          }),
            },
        };
    }
    return {
        ...question,
        name: question.name.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
            const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
            if (!q) return '';
            return `{Q${q.order}}`;
        }),
        rows: question.rows.map((row) => {
            return {
                ...row,
                value:
                    typeof row.value !== 'string'
                        ? row.value
                        : row.value.toString().replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                              const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                              if (!q) return '';
                              return `{Q${q.order}}`;
                          }),
            };
        }),
        cols: question.cols.map((col) => {
            return {
                ...col,
                name:
                    typeof col.name !== 'string'
                        ? col.name
                        : col.name.toString().replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                              const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                              if (!q) return '';
                              return `{Q${q.order}}`;
                          }),
            };
        }),
        conditions: question.conditions.map((cond) => ({
            ...cond,
            completeText:
                typeof cond.completeText !== 'string'
                    ? cond.completeText
                    : cond.completeText.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                          const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                          if (!q) return '';
                          return `{Q${q.order}}`;
                      }),
            disqualText:
                typeof cond.disqualText !== 'string'
                    ? cond.disqualText
                    : cond.disqualText.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                          const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
                          if (!q) return '';
                          return `{Q${q.order}}`;
                      }),
        })),
    };
};
